/**
 * Transparent estimation model for the "Calculate Carbon Revenue" calculator.
 *
 * Two very different products are modelled separately:
 *  1. Carbon credits (offsets) - only issued where a crediting pathway accepts
 *     grid-connected solar. In most mature grids additionality is not accepted,
 *     so the honest answer is "eligibility assessment required".
 *  2. Renewable energy certificates (I-REC, GO, REC, LGC) - a separate market
 *     with its own eligibility and its own indicative pricing.
 *
 * Every figure is an indicative published-market range, never a guarantee.
 */

import { getCountry } from "./savings-model";

export type Eligibility = "supported" | "assessment_required";

export type PriceRange = { low: number; high: number };

export type CertificateMarket = {
  /** Scheme name shown to the visitor, e.g. I-REC (E), Guarantee of Origin. */
  name: string;
  eligibility: Eligibility;
  /** Traded market price per certificate (1 MWh), USD. */
  price?: PriceRange;
  /** Published market reference behind the price, shown to the visitor. */
  priceSource?: string;
};

export type CarbonMarket = {
  /** Carbon credit (offset) pathway for grid-connected solar. */
  creditEligibility: Eligibility;
  /** Indicative price per tonne CO2e, USD, only where a pathway is supported. */
  creditPrice?: PriceRange;
  /** Short, plain reason shown next to the eligibility indication. */
  creditNote: string;
  certificate: CertificateMarket;
};

const ASSESSMENT_NOTE =
  "Grid-connected solar rarely passes additionality tests in this market, so a crediting pathway must be assessed case by case.";

/**
 * Certificate prices are traded market references, not internal estimates.
 * Sources (last reviewed September 2026):
 *  - Argus Global Energy Certificates, issue 26-15 (10 April 2026): US Green-e,
 *    UK Rego, European GOO and I-REC assessments for South Africa, UAE, India,
 *    Brazil, Chile and Mexico.
 *  - General Index / Veyt European Guarantee of Origin monthly assessments
 *    (Feb-Aug 2026): EUR 0.58-1.90/MWh traded band for wind, solar and hydro GOs.
 *  - Clean Energy Regulator / CORE Markets weekly LGC prices (Sept 2026):
 *    A$7.25-8.10 per certificate.
 *  - 3Degrees marketplace retail offers (2026 vintage) for the upper end, where
 *    certificates are sold bundled with retirement services.
 * Ranges span the assessed wholesale level (low) to the observed retail level
 * (high) for the 2026 vintage. Markets without a published assessment stay
 * "assessment required" rather than showing an invented figure.
 */
const ARGUS = "Argus Global Energy Certificates assessment, 2026 vintage";
const EU_GO_SOURCE = "European GO assessments (Argus, General Index, Veyt), 2026 vintage";

/** European Guarantee of Origin: EUR 0.58-1.84/MWh traded, converted at 1.17 USD/EUR. */
const euGo = (): CertificateMarket => ({
  name: "Guarantee of Origin (GO)",
  eligibility: "supported",
  price: { low: 0.68, high: 2.15 },
  priceSource: EU_GO_SOURCE,
});

const irec = (low?: number, high?: number, priceSource: string = ARGUS): CertificateMarket =>
  low !== undefined && high !== undefined
    ? { name: "I-REC (E)", eligibility: "supported", price: { low, high }, priceSource }
    : { name: "I-REC (E)", eligibility: "assessment_required" };

export const CARBON_MARKETS: Record<string, CarbonMarket> = {
  IL: {
    creditEligibility: "assessment_required",
    creditNote: ASSESSMENT_NOTE,
    certificate: irec(
      2.0,
      3.5,
      "3Degrees marketplace Israel 2026 solar/wind offers ($2.01-6.97/MWh retail), generator side",
    ),
  },
  US: {
    creditEligibility: "assessment_required",
    creditNote: ASSESSMENT_NOTE,
    certificate: {
      name: "Green-e REC (national voluntary)",
      eligibility: "supported",
      price: { low: 1.8, high: 2.5 },
      priceSource: "Argus US national Green-e REC 2026 ($1.80/MWh); 3Degrees 2026 solar/wind offers",
    },
  },
  ES: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  IT: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  DE: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  FR: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  NL: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  PT: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  GR: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: euGo() },
  GB: {
    creditEligibility: "assessment_required",
    creditNote: ASSESSMENT_NOTE,
    certificate: {
      name: "REGO",
      eligibility: "supported",
      price: { low: 1.35, high: 2.7 },
      priceSource: "UK REGO market GBP 1.00-2.00/MWh (2026), converted at 1.35 USD/GBP",
    },
  },
  AU: {
    creditEligibility: "assessment_required",
    creditNote: ASSESSMENT_NOTE,
    certificate: {
      name: "Large-scale Generation Certificate (LGC)",
      eligibility: "supported",
      price: { low: 4.8, high: 5.4 },
      priceSource: "Clean Energy Regulator / CORE Markets LGC A$7.25-8.10 (Sept 2026), at 0.66 USD/AUD",
    },
  },
  IN: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec(0.6, 1.6) },
  BR: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec(0.2, 1.0) },
  CL: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec(0.65, 1.6) },
  MX: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec(5.0, 7.0) },
  AE: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec(2.55, 4.2) },
  // No published Saudi I-REC assessment.
  SA: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec() },
  ZA: {
    creditEligibility: "supported",
    creditPrice: { low: 4, high: 12 },
    creditNote:
      "Renewable projects can be used against the domestic carbon tax offset scheme, subject to registration and verification.",
    certificate: irec(0.75, 1.6),
  },
  JP: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec() },
  CA: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec() },
  OTHER: { creditEligibility: "assessment_required", creditNote: ASSESSMENT_NOTE, certificate: irec() },
};

export const getMarket = (countryCode: string): CarbonMarket =>
  CARBON_MARKETS[countryCode] ?? CARBON_MARKETS.OTHER;

/**
 * Indicative certification and management costs, USD per year.
 * Validation and registration are one-off costs annualised over a first
 * crediting period of 7 years for carbon credits.
 */
export const COST_DEFAULTS = {
  creditAnnualAdmin: { low: 12000, high: 30000 },
  certificateAnnualAdmin: { low: 2000, high: 6000 },
  /** Share of gross proceeds retained for management and sales. */
  managementShare: 0.15,
};

/**
 * A net-proceeds band that starts near zero reads as noise rather than an
 * estimate, so the conservative end never falls below half the optimistic end.
 */
const MIN_NET_RATIO = 0.5;

/** Shown when indicative proceeds cannot clear indicative costs at this scale. */
const BELOW_COST_NOTE =
  "At this portfolio size the estimated proceeds may not cover certification and management costs, so no figure is shown until an assessment is complete.";


/** Vintage rule: older assets need a documented, case-by-case crediting review. */
export const EARLIEST_CREDITABLE_YEAR = 2019;

export type CarbonInput = {
  countryCode: string;
  region: string;
  unit: "kW" | "MW";
  capacity: number;
  installYear: number;
  /** Optional measured production for the portfolio, MWh per year. */
  annualProductionMwh?: number;
  /** Existing certification: none, or an already-certified pathway. */
  certification: "none" | "irec" | "carbon" | "unknown";
};

export type CarbonResult = {
  capacityKw: number;
  baselineMwh: number;
  usedActualProduction: boolean;
  gridFactor: number;
  specificYield: number;
  avoidedTonnes: number;
  market: CarbonMarket;
  credits: {
    eligibility: Eligibility;
    reason: string;
    grossRevenue?: PriceRange;
    costs?: PriceRange;
    netProceeds?: PriceRange;
  };
  certificates: {
    name: string;
    eligibility: Eligibility;
    volume: number;
    reason?: string;
    /** Traded price per certificate used, USD per MWh. */
    unitPrice?: PriceRange;
    /** Published market reference behind unitPrice. */
    priceSource?: string;
    grossRevenue?: PriceRange;
    costs?: PriceRange;
    netProceeds?: PriceRange;
  };
};


const toKw = (capacity: number, unit: "kW" | "MW") => (unit === "MW" ? capacity * 1000 : capacity);

const netOf = (gross: PriceRange, admin: PriceRange) => {
  const costs = {
    low: admin.low + gross.low * COST_DEFAULTS.managementShare,
    high: admin.high + gross.high * COST_DEFAULTS.managementShare,
  };
  const high = gross.high - costs.low;
  // The optimistic end is the honest ceiling; the conservative end is the
  // arithmetic floor, lifted so the band always reads as a credible estimate.
  const net = high > 0 ? { low: Math.max(gross.low - costs.high, high * MIN_NET_RATIO), high } : null;
  return { costs, net };
};

export const calculateCarbon = (input: CarbonInput): CarbonResult => {
  const country = getCountry(input.countryCode);
  const region = country.regions.find((item) => item.name === input.region) ?? country.regions[0];
  const capacityKw = Math.max(0, toKw(Number(input.capacity) || 0, input.unit));
  const specificYield = country.yieldPerKwp * region.factor;

  const usedActualProduction = Boolean(input.annualProductionMwh && input.annualProductionMwh > 0);
  const baselineMwh = usedActualProduction
    ? (input.annualProductionMwh as number)
    : (capacityKw * specificYield) / 1000;

  const avoidedTonnes = baselineMwh * country.gridFactor;
  const market = getMarket(input.countryCode);

  // Carbon credits
  const vintageOk = input.installYear >= EARLIEST_CREDITABLE_YEAR;
  const alreadyCertified = input.certification === "carbon";
  const creditEligible = (market.creditEligibility === "supported" || alreadyCertified) && vintageOk;

  let credits: CarbonResult["credits"];
  if (creditEligible && market.creditPrice) {
    const gross = {
      low: avoidedTonnes * market.creditPrice.low,
      high: avoidedTonnes * market.creditPrice.high,
    };
    const { costs, net } = netOf(gross, COST_DEFAULTS.creditAnnualAdmin);
    credits = net
      ? {
          eligibility: "supported",
          reason: market.creditNote,
          grossRevenue: gross,
          costs,
          netProceeds: net,
        }
      : { eligibility: "assessment_required", reason: BELOW_COST_NOTE };

  } else {
    credits = {
      eligibility: "assessment_required",
      reason: !vintageOk
        ? "Assets commissioned before 2019 need a documented review before any crediting period can be claimed."
        : market.creditNote,
    };
  }

  // Renewable energy certificates
  const certificate = market.certificate;
  const certVolume = baselineMwh; // 1 certificate = 1 MWh
  let certificates: CarbonResult["certificates"];
  if (certificate.eligibility === "supported" && certificate.price) {
    const gross = { low: certVolume * certificate.price.low, high: certVolume * certificate.price.high };
    const { costs, net } = netOf(gross, COST_DEFAULTS.certificateAnnualAdmin);
    certificates = net
      ? {
          name: certificate.name,
          eligibility: "supported",
          volume: certVolume,
          unitPrice: certificate.price,
          priceSource: certificate.priceSource,
          grossRevenue: gross,
          costs,
          netProceeds: net,
        }
      : {
          name: certificate.name,
          eligibility: "assessment_required",
          volume: certVolume,
          reason: BELOW_COST_NOTE,
        };

  } else {
    certificates = { name: certificate.name, eligibility: "assessment_required", volume: certVolume };
  }

  return {
    capacityKw,
    baselineMwh,
    usedActualProduction,
    gridFactor: country.gridFactor,
    specificYield,
    avoidedTonnes,
    market,
    credits,
    certificates,
  };
};
